import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Mail, Phone, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, adminHeaders, clearAdminToken, getAdminToken } from "@/lib/api";

const STATUS_OPTIONS = ["new", "contacted", "qualified", "referred", "closed"];

function b64ToBlob(b64, contentType) {
  const byteChars = atob(b64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
  return new Blob([new Uint8Array(byteNumbers)], { type: contentType || "application/octet-stream" });
}

async function downloadAttachment(lead) {
  try {
    const res = await api.get(`/admin/leads/${lead.id}/attachment`, { headers: adminHeaders() });
    const blob = b64ToBlob(res.data.content_b64, res.data.content_type);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = res.data.filename || "attachment";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Could not download attachment.");
  }
}

export default function AdminDashboard() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!getAdminToken()) {
      nav("/admin/login", { replace: true });
      return;
    }
    setLoading(true);
    api.get("/admin/leads", { headers: adminHeaders() })
      .then((res) => setLeads(res.data))
      .catch((err) => {
        if (err?.response?.status === 401) {
          clearAdminToken();
          nav("/admin/login", { replace: true });
          return;
        }
        toast.error("Could not load leads.");
      })
      .finally(() => setLoading(false));
  }, [nav]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/leads", { headers: adminHeaders() });
      setLeads(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearAdminToken();
        nav("/admin/login", { replace: true });
        return;
      }
      toast.error("Could not load leads.");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = (leadId, newStatus) => {
    const path = "/admin/leads/" + leadId + "?status=" + encodeURIComponent(newStatus);
    api.patch(path, {}, { headers: adminHeaders() })
      .then(() => {
        setLeads((ls) => ls.map((l) => l.id === leadId ? { ...l, status: newStatus } : l));
        setSelected((sel) => (sel && sel.id === leadId ? { ...sel, status: newStatus } : sel));
        toast.success("Lead marked " + newStatus + ".");
      })
      .catch(() => toast.error("Update failed."));
  };

  const remove = async (lead) => {
    if (!window.confirm(`Delete lead from ${lead.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/leads/${lead.id}`, { headers: adminHeaders() });
      setLeads((ls) => ls.filter((l) => l.id !== lead.id));
      if (selected?.id === lead.id) setSelected(null);
      toast.success("Lead deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const logout = () => {
    clearAdminToken();
    nav("/admin/login");
  };

  const filtered = filter === "all" ? leads : leads.filter((l) => l.status === filter);
  const counts = STATUS_OPTIONS.reduce((acc, s) => { acc[s] = leads.filter(l => l.status === s).length; return acc; }, {});

  return (
    <div className="min-h-screen bg-[#F8F8F5]" data-testid="admin-dashboard">
      <div className="bg-[#1A1A1A] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white flex items-center justify-center">
              <span className="font-heading text-[#1A1A1A] text-base font-bold tracking-tight">R</span>
            </div>
            <div>
              <div className="font-heading text-lg font-medium tracking-tight">RVA Signs &middot; Admin</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/50 mt-1">Lead Inbox</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={load} className="text-xs text-white/70 hover:text-[#F97316] inline-flex items-center gap-2" data-testid="admin-refresh">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button onClick={logout} className="border border-white/20 hover:border-[#F97316] hover:text-[#F97316] text-xs px-4 py-2 inline-flex items-center gap-2 transition-colors" data-testid="admin-logout">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="grid grid-cols-2 sm:grid-cols-6 border-l border-t border-[#E2E8F0] mb-8" data-testid="admin-stats">
          <button onClick={() => setFilter("all")} className={`border-r border-b border-[#E2E8F0] p-5 text-left ${filter === "all" ? "bg-white" : "bg-[#F8F8F5] hover:bg-white"}`} data-testid="filter-all">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#2B2F33]/60 font-bold">All</div>
            <div className="font-heading text-3xl font-medium text-[#1A1A1A] mt-2">{leads.length}</div>
          </button>
          {STATUS_OPTIONS.map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`border-r border-b border-[#E2E8F0] p-5 text-left capitalize ${filter === s ? "bg-white" : "bg-[#F8F8F5] hover:bg-white"}`} data-testid={`filter-${s}`}>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#2B2F33]/60 font-bold">{s}</div>
              <div className="font-heading text-3xl font-medium text-[#1A1A1A] mt-2">{counts[s] || 0}</div>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 bg-white border border-[#E2E8F0]">
            <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
              <div className="font-heading text-lg tracking-tight font-medium text-[#1A1A1A]">Leads {filter !== "all" && <span className="text-[#F97316] capitalize">/ {filter}</span>}</div>
              <div className="text-xs text-[#2B2F33]/60">{filtered.length} of {leads.length}</div>
            </div>
            {loading ? (
              <div className="p-10 text-center text-sm text-[#2B2F33]/60" data-testid="admin-loading">Loading...</div>
            ) : filtered.length === 0 ? (
              <div className="p-10 text-center text-sm text-[#2B2F33]/60" data-testid="admin-empty">No leads in this view yet.</div>
            ) : (
              <ul className="divide-y divide-[#E2E8F0]" data-testid="admin-leads-list">
                {filtered.map((l) => (
                  <li key={l.id}>
                    <button
                      onClick={() => setSelected(l)}
                      className={`w-full text-left px-6 py-5 flex items-center justify-between gap-4 hover:bg-[#F8F8F5] transition-colors ${selected?.id === l.id ? "bg-[#F8F8F5]" : ""}`}
                      data-testid={`lead-row-${l.id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-heading text-base font-medium text-[#1A1A1A] tracking-tight truncate">{l.name}{l.company && <span className="text-[#2B2F33]/60"> &middot; {l.company}</span>}</div>
                        <div className="text-xs text-[#2B2F33]/60 mt-1 truncate">{l.project_type} {l.budget_range && `· ${l.budget_range}`}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className={`text-[10px] tracking-[0.18em] uppercase font-bold ${l.status === "new" ? "text-[#F97316]" : "text-[#2B2F33]/60"}`}>{l.status}</div>
                        <div className="text-[11px] text-[#2B2F33]/50 mt-1">{new Date(l.created_at).toLocaleDateString()}</div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="lg:col-span-5">
            {selected ? (
              <div className="bg-white border border-[#E2E8F0] sticky top-6" data-testid="lead-detail">
                <div className="bg-[#1A1A1A] text-white px-6 py-5">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#F97316] font-bold">Lead</div>
                  <div className="font-heading text-2xl tracking-tight font-medium mt-2">{selected.name}</div>
                  {selected.company && <div className="text-sm text-white/70 mt-1">{selected.company}</div>}
                </div>
                <div className="p-6 space-y-5">
                  <div className="flex flex-col gap-3 text-sm">
                    <a href={`tel:${selected.phone}`} className="flex items-center gap-3 text-[#1A1A1A] hover:text-[#F97316]" data-testid="lead-call">
                      <Phone className="w-4 h-4 text-[#F97316]" /> {selected.phone}
                    </a>
                    <a href={`mailto:${selected.email}`} className="flex items-center gap-3 text-[#1A1A1A] hover:text-[#F97316]" data-testid="lead-email">
                      <Mail className="w-4 h-4 text-[#F97316]" /> {selected.email}
                    </a>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-[#E2E8F0]">
                    <Detail label="Project Type" value={selected.project_type} />
                    <Detail label="Budget" value={selected.budget_range || "."} />
                    <Detail label="Already Has Quote" value={selected.has_quote || "."} />
                    <Detail label="Address" value={selected.project_address || "."} className="col-span-2" />
                  </div>

                  {selected.attachment_filename ? (
                    <div className="pt-4 border-t border-[#E2E8F0]">
                      <div className="text-[10px] tracking-[0.22em] uppercase text-[#2B2F33]/60 font-bold mb-2">Attached File</div>
                      <button
                        onClick={() => downloadAttachment(selected)}
                        data-testid="lead-attachment-download"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A1A1A] hover:text-[#F97316] underline underline-offset-4"
                      >
                        {selected.attachment_filename}
                        <span className="text-[#5F6B73] text-xs">({Math.round((selected.attachment_size || 0) / 1024)} KB)</span>
                      </button>
                    </div>
                  ) : null}

                  <div>
                    <div className="text-[10px] tracking-[0.22em] uppercase text-[#2B2F33]/60 font-bold mb-2">Description</div>
                    <div className="text-sm text-[#2B2F33]/90 leading-relaxed whitespace-pre-wrap" data-testid="lead-description">{selected.project_description || "."}</div>
                  </div>

                  <div className="pt-4 border-t border-[#E2E8F0]">
                    <div className="text-[10px] tracking-[0.22em] uppercase text-[#2B2F33]/60 font-bold mb-3">Status</div>
                    <div className="flex flex-wrap gap-2">
                      {STATUS_OPTIONS.map((s) => (
                        <button key={s} onClick={() => updateStatus(selected.id, s)}
                          data-testid={`status-${s}`}
                          className={`text-xs capitalize px-3 py-2 border transition-colors ${selected.status === s ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "border-[#E2E8F0] text-[#2B2F33] hover:border-[#1A1A1A]"}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-[#E2E8F0] flex items-center justify-between">
                    <div className="text-xs text-[#2B2F33]/55">Submitted {new Date(selected.created_at).toLocaleString()}</div>
                    <button onClick={() => remove(selected)} className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1" data-testid="lead-delete">
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white border border-[#E2E8F0] p-10 text-center text-sm text-[#2B2F33]/60" data-testid="lead-detail-empty">
                Select a lead to view details.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value, className = "" }) {
  return (
    <div className={className}>
      <div className="text-[10px] tracking-[0.22em] uppercase text-[#2B2F33]/60 font-bold">{label}</div>
      <div className="text-sm text-[#2B2F33] mt-1.5">{value}</div>
    </div>
  );
}
